import { useMutation, useQuery } from "@apollo/client";
import {
  claimLoginRewardGQL,
  generateUploadURLGQL,
  loginRewardsGQL,
  LoginRewardStatus,
  meGQL,
  Query,
} from "@onmo/graphql/graphql";
import { useCallback, useEffect, useState } from "react";
import { BucketType, Achievement } from "@onmo/graphql/graphql";
import html2canvas from "html2canvas";
import { createShortUrlGQL, useLegacyMutation } from "@onmo/graphql/legacyGraphql";
import { User } from "../../../models/user";
import { addPopupQueue } from "../../../store/slices/popup";
import { useAppDispatch, useAppSelector } from "../../../components/hooks";
import { PopupType } from "../../../constants/popup";
import { UserState } from "@onmo/auth";
import { useFetchNewAchievement } from "../../useFetchNewAchievement";
import { FEATURES } from "../../../types/theme";

export const useFetchLoginReward = () => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.theme);
  const hideDailyReward =
    theme?.hideFeature?.includes(FEATURES.dailyReward) || false;
  const { data: loginRewardsData } = useQuery<Query>(loginRewardsGQL, {
    fetchPolicy: "network-only",
    skip: status !== UserState.SIGNIN || hideDailyReward,
  });

  useEffect(() => {
    if (hideDailyReward) return;
    if (!loginRewardsData?.loginRewards) return;
    const activeRewardIndex = User.getCurrentDailyReward(
      loginRewardsData?.loginRewards,
    );
    if (
      loginRewardsData?.loginRewards?.[activeRewardIndex]?.claimStatus ===
      LoginRewardStatus.Unclaimed
    ) {
      dispatch(
        addPopupQueue({
          type: PopupType.DailyReward,
        }),
      );
    }
  }, [dispatch, loginRewardsData, hideDailyReward]);

  return { loginRewards: loginRewardsData?.loginRewards || [] };
};

export const useClaimLoginReward = (activeRewardIndex: number) => {
  const [claimLoginRewardMutation] = useMutation(claimLoginRewardGQL);
  const { fetchNewAchivement } = useFetchNewAchievement();
  const [newAchivement, setNewAchivement] = useState<Achievement[]>();

  const claimLoginReward = useCallback(async () => {
    try {
      await claimLoginRewardMutation({
        variables: {
          day: activeRewardIndex,
        },
        refetchQueries: [{ query: meGQL }],
      });
      const newAchivement = await fetchNewAchivement();
      setNewAchivement(newAchivement);
    } catch (error) {
      console.error(error);
    }
  }, [activeRewardIndex, claimLoginRewardMutation, fetchNewAchivement]);

  return { claimLoginReward, newAchivement };
};

export const useCreateShareUrl = () => {
  const [loading, setLoading] = useState(false);
  const [generateUploadURL] = useMutation(generateUploadURLGQL);
  const [createShortUrl] = useLegacyMutation(createShortUrlGQL);
  const { data: dataMe } = useQuery(meGQL);

  const createShareUrl = useCallback(async (): Promise<string | undefined> => {
    const shareContent = document.getElementById("shareContent");
    if (!shareContent) return;

    setLoading(true);
    try {
      const shareCanvas = html2canvas(shareContent, {
        backgroundColor: "#101113",
        useCORS: true,
      }).then(function (canvas) {
        return canvas;
      });
      const shareData = (await shareCanvas).toDataURL();

      const binary = atob(shareData.split(",")[1]);
      const array = [];
      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
      }

      const imgBlob = new Blob([new Uint8Array(array)], { type: "image/png" });
      const objectPath = `/share/${dataMe?.me?.id}/${Date.now()}.png`;
      const { data: uploadUrlData } = await generateUploadURL({
        variables: {
          input: {
            objectPath: `public${objectPath}`,
            bucketType: BucketType.Data,
            contentType: "image/png",
          },
        },
      });

      await fetch(uploadUrlData?.generateUploadURL?.url, {
        method: "PUT",
        body: imgBlob,
      });

      const { data: response } = await createShortUrl({
        variables: {
          createShortUrlInput: {
            path: "/",
            productId: "tgp",
            imagePath: objectPath,
            title: "Gaming Platform",
          },
        },
      });

      console.log(
        `Created share image url - ${response?.createShortUrl?.url}`,
      );

      if (response?.createShortUrl?.url?.startsWith("https")) {
        return response?.createShortUrl?.url;
      } else {
        return "https://" + response?.createShortUrl?.url;
      }
    } catch (e) {
      console.log(`Fail to save image of game `, e);
    }
    setLoading(false);
  }, [createShortUrl, dataMe?.me?.id, generateUploadURL]);

  return { createShareUrl, loading };
};
